import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useId, useState, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import { RecordPicker } from '@/card/RecordPicker'
import { ApiError } from '@/lib/api'
import { fetchTemplatesFor } from '@/mail/api'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  Grip,
  LayerButtons,
  Notice,
  OnOffBadge,
  PrimaryButton,
  SkeletonList,
  Switch,
  isForbidden,
  moveId,
  useAdminEditing,
} from './AdminShell'
import {
  createAutomationRule,
  evaluateAutomationRule,
  fetchAutomationCatalog,
  fetchAutomationRules,
  fetchAutomationRuns,
  reorderAutomationRules,
  setAutomationRuleActive,
  updateAutomationRule,
  type AutomationActionParam,
  type AutomationCatalog,
  type AutomationEntity,
  type AutomationExplanation,
  type AutomationRule,
  type AutomationRuleBody,
  type AutomationRun,
} from './automationApi'
import {
  LIST_OPS,
  NO_VALUE_OPS,
  PREVIOUS,
  actionJson,
  conditionsJson,
  draftActions,
  draftConditions,
  draftTimer,
  emptyGroup,
  fieldType,
  nextKey,
  timerJson,
  type ActionDraft,
  type CondGroup,
  type CondLeaf,
  type ConditionsDraft,
  type TimerDraft,
} from './automationModel'

/**
 * Конструктор автосценариев (тикет #116; движок и REST — #115).
 *
 * Всё, из чего собирается правило, — сущности, триггеры, поля, операторы, действия и их
 * параметры, — приходит из каталога сервера, а не из списка на клиенте: поле, которое сервер
 * не знает, сохранилось бы и молча не совпало ни с одной записью, а оператор, которого он не
 * примет для этого типа, учил бы игнорировать отказы. Сервер проверяет правило при
 * сохранении и отказывает кодом и словами — экран показывает и то и другое.
 *
 * Журнал отвечает на вопрос критерия «почему правило не сработало на этой сделке»: запуски
 * сужаются до записи на сервере (последние пятьдесят запусков занятого правила могут её не
 * содержать), а проверка на записи показывает вердикт по каждому условию с фактическим
 * значением — первая причина из журнала не говорит, что за ней стоит вторая.
 */

/** Записи, которые можно найти по названию: у задач и обращений своего списка нет — по id. */
const PICKERS: Record<string, { entity: string; search: string; extra?: string }> = {
  deal: { entity: 'sales.deal', search: 'number', extra: 'title' },
  lead: { entity: 'sales.lead', search: 'title' },
}

const RULES_KEY = ['admin-automation-rules']

type T = (key: string, params?: Record<string, string | number>) => string

/** Перевод кода из каталога; кода, которого нет в словаре, — сам код, а не пустота. */
const label = (t: T, key: string, fallback: string) => {
  const text = t(key)
  return text === key ? fallback : text
}

function failureText(t: T, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return t('adm.auto.conflict')
    return error.code ? `${error.message} · ${error.code}` : error.message
  }
  return error instanceof Error ? error.message : t('app.error')
}

const groupKey = (rule: AutomationRule) => `${rule.entityType}|${rule.trigger}`

export function AutomationPage() {
  const { t } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const catalog = useQuery({ queryKey: ['admin-automation-catalog'], queryFn: ({ signal }) => fetchAutomationCatalog(signal) })
  const rules = useQuery({ queryKey: RULES_KEY, queryFn: ({ signal }) => fetchAutomationRules(signal) })
  const [editor, setEditor] = useState<{ rule: AutomationRule | null } | null>(null)
  const [journal, setJournal] = useState<AutomationRule | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const refresh = () => {
    setNotice(null)
    void queryClient.invalidateQueries({ queryKey: RULES_KEY })
  }
  const toggle = useMutation({
    mutationFn: (rule: AutomationRule) => setAutomationRuleActive(rule.id, !rule.active),
    onSuccess: refresh,
    onError: (error) => setNotice(failureText(t, error)),
  })
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderAutomationRules(ids),
    onSuccess: refresh,
    onError: (error) => {
      setNotice(failureText(t, error))
      void queryClient.invalidateQueries({ queryKey: RULES_KEY })
    },
  })

  const entities = catalog.data?.entities ?? []
  const entityOf = (code: string) => entities.find((entity) => entity.code === code)
  const list = rules.data ?? []
  const groups: { key: string; rules: AutomationRule[] }[] = []
  for (const rule of list) {
    const key = groupKey(rule)
    const group = groups.find((candidate) => candidate.key === key)
    if (group) group.rules.push(rule)
    else groups.push({ key, rules: [rule] })
  }
  const nextOrder = (entityType: string, trigger: string) => {
    const orders = list.filter((rule) => rule.entityType === entityType && rule.trigger === trigger).map((rule) => rule.executionOrder)
    return orders.length === 0 ? 10 : Math.max(...orders) + 10
  }
  const forbidden = isForbidden(rules.error) || isForbidden(catalog.error)

  return (
    <AdminPage
      titleKey="adm.auto.title"
      leadKey="adm.auto.lead"
      count={list.length > 0 ? t('adm.auto.count', { count: list.length }) : undefined}
      readOnly={!editing.mayEdit}
      roKey="adm.auto.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && catalog.data && (
        <PrimaryButton label={t('adm.auto.add')} onClick={() => setEditor({ rule: null })} />
      )}
    >
      <Notice text={notice} />
      <AdminState
        isLoading={rules.isLoading || catalog.isLoading}
        error={rules.error ?? catalog.error}
        onRetry={() => {
          void rules.refetch()
          void catalog.refetch()
        }}
        forbiddenTitleKey="adm.auto.forbidden"
        errorTitleKey="adm.auto.error"
        skeleton={<SkeletonList rows={5} />}
      >
        {list.length === 0 && (
          <StateView boxed kind="empty" title={t('adm.auto.empty')} description={t('adm.auto.empty.text')} />
        )}
        {groups.map((group) => {
          const first = group.rules[0]
          if (!first) return null
          const entity = entityOf(first.entityType)
          const heading = `${entity?.label ?? first.entityType} · ${label(t, `adm.auto.trigger.${first.trigger}`, first.trigger)}`
          const ids = group.rules.map((rule) => rule.id)
          const move = (id: string, target: number) => {
            const next = moveId(ids, id, target)
            if (next.join() !== ids.join()) reorder.mutate(next)
          }
          return (
            <section key={group.key} className="stack" style={{ gap: 6, marginBottom: 18 }}>
              <h2 className="h3">{heading}</h2>
              <div className="sort-list" role="list" aria-label={heading} aria-busy={reorder.isPending || undefined}>
                {group.rules.map((rule, index) => (
                  <div key={rule.id} role="listitem" data-testid={`rule-${rule.id}`}
                       className={rule.active ? 'sort-row' : 'sort-row is-off'} style={{ minHeight: 56 }}>
                    {editing.mayEdit && (
                      <Grip label={t('adm.auto.move', { name: rule.name })} disabled={reorder.isPending}
                            onMove={(step) => move(rule.id, index + step)} />
                    )}
                    <span className="ord" style={editing.mayEdit ? undefined : { marginLeft: 8 }}>{index + 1}</span>
                    <span className="adm-grow stack" style={{ gap: 4, padding: '4px 0' }}>
                      <span className={rule.active ? 'strong' : 'strong muted'}>{rule.name}</span>
                      <span className="t-sm secondary">{conditionsSummary(t, rule, entity, catalog.data)}</span>
                      <span className="row-wrap" style={{ gap: 4 }}>
                        {draftActions(rule.actions).map((action) => (
                          <span key={action.key} className="badge">{label(t, `adm.auto.action.${action.type}`, action.type)}</span>
                        ))}
                      </span>
                    </span>
                    <span className="adm-col" style={{ width: 110 }}>
                      {editing.mayEdit ? (
                        <Switch checked={rule.active} disabled={toggle.isPending} ariaLabel={t('adm.auto.enabledOf', { name: rule.name })}
                                onChange={() => toggle.mutate(rule)} />
                      ) : (
                        <OnOffBadge on={rule.active} />
                      )}
                    </span>
                    <span className="row" style={{ gap: 4 }}>
                      <button type="button" className="btn btn-ghost btn-sm" aria-label={t('adm.auto.journalOf', { name: rule.name })}
                              onClick={() => setJournal(rule)}>
                        <Icon name="clock" size="sm" />{t('adm.auto.journal')}
                      </button>
                      {editing.mayEdit && (
                        <button type="button" className="btn btn-ghost btn-sm" aria-label={t('adm.auto.editOf', { name: rule.name })}
                                onClick={() => setEditor({ rule })}>
                          <Icon name="adm-edit" size="sm" />{t('admin.edit')}
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )
        })}
      </AdminState>

      {editor && catalog.data && (
        // Keyed by the rule: the draft belongs to the rule it was opened for, and a second
        // rule opened in the same panel must not inherit the first one's edits.
        <RuleSheet key={editor.rule?.id ?? 'new'} rule={editor.rule} catalog={catalog.data} nextOrder={nextOrder}
                   onClose={() => setEditor(null)}
                   onSaved={() => {
                     setEditor(null)
                     refresh()
                   }} />
      )}
      {journal && (
        <JournalSheet key={journal.id} rule={journal} entity={entityOf(journal.entityType)} catalog={catalog.data}
                      onClose={() => setJournal(null)} />
      )}
    </AdminPage>
  )
}

// ------------------------------------------------------------------ summary

function fieldLabel(t: T, entity: AutomationEntity | undefined, field: string): string {
  const previous = field.startsWith(PREVIOUS)
  const base = previous ? field.slice(PREVIOUS.length) : field
  const name = entity?.fields?.find((candidate) => candidate.name === base)?.label ?? base
  return previous ? t('adm.auto.previous', { label: name }) : name
}

function opSign(catalog: AutomationCatalog | undefined, op: string): string {
  return catalog?.operators?.find((candidate) => candidate.code === op)?.sign ?? op
}

function describeGroup(t: T, group: CondGroup, entity: AutomationEntity | undefined, catalog: AutomationCatalog | undefined): string {
  return group.items.map((item) => item.kind === 'leaf'
    ? [fieldLabel(t, entity, item.field), opSign(catalog, item.op), NO_VALUE_OPS.has(item.op) ? '' : item.value].filter(Boolean).join(' ')
    : `(${describeGroup(t, item, entity, catalog)})`)
    .join(group.mode === 'all' ? ` ${t('adm.auto.and')} ` : ` ${t('adm.auto.or')} `)
}

function conditionsSummary(t: T, rule: AutomationRule, entity: AutomationEntity | undefined, catalog: AutomationCatalog | undefined): string {
  const draft = draftConditions(rule.conditions)
  if (draft.raw !== undefined) return t('adm.auto.rawSummary')
  if (draft.root.items.length === 0) return t('adm.auto.always')
  return describeGroup(t, draft.root, entity, catalog)
}

// ------------------------------------------------------------------ editor

interface RuleDraft {
  name: string
  description: string
  entityType: string
  trigger: string
  conditions: ConditionsDraft
  actions: ActionDraft[]
  timer: TimerDraft
  active: boolean
}

function draftOf(rule: AutomationRule | null, catalog: AutomationCatalog): RuleDraft {
  const entity = catalog.entities?.find((candidate) => candidate.code === rule?.entityType) ?? catalog.entities?.[0]
  if (!rule) {
    return {
      name: '', description: '', entityType: entity?.code ?? '', trigger: entity?.triggers?.[0] ?? '',
      conditions: { root: emptyGroup() }, actions: [], timer: draftTimer(null, entity?.anchors), active: true,
    }
  }
  return {
    name: rule.name,
    description: rule.description ?? '',
    entityType: rule.entityType,
    trigger: rule.trigger,
    conditions: draftConditions(rule.conditions),
    actions: draftActions(rule.actions),
    timer: draftTimer(rule.timer, entity?.anchors),
    active: rule.active,
  }
}

function RuleSheet({ rule, catalog, nextOrder, onClose, onSaved }: {
  rule: AutomationRule | null
  catalog: AutomationCatalog
  nextOrder: (entityType: string, trigger: string) => number
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const [draft, setDraft] = useState<RuleDraft>(() => draftOf(rule, catalog))
  const [error, setError] = useState<string | null>(null)
  const entity = catalog.entities?.find((candidate) => candidate.code === draft.entityType)
  const actionCodes = entity?.actions ?? catalog.actions ?? []

  const save = useMutation({
    mutationFn: () => {
      let conditions: unknown
      try {
        conditions = conditionsJson(draft.conditions, entity)
      } catch {
        throw new Error(t('adm.auto.rawInvalid'))
      }
      const body: AutomationRuleBody = {
        name: draft.name.trim(),
        description: draft.description.trim() === '' ? null : draft.description.trim(),
        entityType: draft.entityType,
        trigger: draft.trigger,
        conditions,
        actions: draft.actions.map((action) => actionJson(action, catalog.actionParams?.[action.type])),
        timer: draft.trigger === 'scheduled' ? timerJson(draft.timer) : null,
        active: draft.active,
      }
      return rule
        ? updateAutomationRule(rule.id, { ...body, version: rule.version })
        : createAutomationRule({ ...body, executionOrder: nextOrder(draft.entityType, draft.trigger) })
    },
    onSuccess: onSaved,
    onError: (failure) => setError(failureText(t, failure)),
  })

  const setEntity = (code: string) => {
    const next = catalog.entities?.find((candidate) => candidate.code === code)
    // Conditions name fields of one entity: carried over, they would compare fields that are not there.
    setDraft({
      ...draft, entityType: code, trigger: next?.triggers?.[0] ?? '', conditions: { root: emptyGroup() },
      actions: [], timer: draftTimer(null, next?.anchors),
    })
  }

  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t('adm.auto.overline')}
      title={rule ? rule.name : t('adm.auto.add')}
      onClose={onClose}
      hint={t('adm.auto.rearmHint')}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                            disabled={draft.name.trim() === '' || draft.actions.length === 0} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('adm.auto.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('adm.auto.name')} htmlFor="auto-name" required>
          <input id="auto-name" className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label={t('adm.auto.description')} htmlFor="auto-description" help={t('adm.auto.description.help')}>
          <textarea id="auto-description" className="input" rows={2} value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </Field>
        <div className="adm-two">
          <Field label={t('adm.auto.entity')} htmlFor="auto-entity" help={rule ? t('adm.auto.entityImmutable') : undefined}>
            <select id="auto-entity" className="input" value={draft.entityType} disabled={rule != null}
                    onChange={(e) => setEntity(e.target.value)}>
              {(catalog.entities ?? []).map((candidate) => (
                <option key={candidate.code} value={candidate.code}>{candidate.label}</option>
              ))}
            </select>
          </Field>
          <Field label={t('adm.auto.trigger')} htmlFor="auto-trigger">
            <select id="auto-trigger" className="input" value={draft.trigger} onChange={(e) => setDraft({ ...draft, trigger: e.target.value })}>
              {(entity?.triggers ?? []).map((code) => (
                <option key={code} value={code}>{label(t, `adm.auto.trigger.${code}`, code)}</option>
              ))}
            </select>
          </Field>
        </div>
        {draft.trigger === 'sla_breached' && entity?.sla != null && (
          <p className="t-sm secondary">{t('adm.auto.slaMeans', { what: entity.sla })}</p>
        )}
        {draft.trigger === 'scheduled' && (
          <TimerEditor timer={draft.timer} entity={entity} onChange={(timer) => setDraft({ ...draft, timer })} />
        )}

        <div className="field" style={{ gap: 6 }}>
          <span className="label">{t('adm.auto.conditions')}</span>
          {draft.conditions.raw !== undefined ? (
            <>
              <span className="t-sm muted">{t('adm.auto.rawText')}</span>
              <textarea className="input mono" rows={8} aria-label={t('adm.auto.rawLabel')} value={draft.conditions.raw}
                        onChange={(e) => setDraft({ ...draft, conditions: { raw: e.target.value } })} />
            </>
          ) : (
            <GroupEditor group={draft.conditions.root} path="" entity={entity} trigger={draft.trigger} catalog={catalog}
                         onChange={(root) => setDraft({ ...draft, conditions: { root } })} />
          )}
        </div>

        <div className="field" style={{ gap: 6 }}>
          <span className="label">{t('adm.auto.actions')}<span className="req" aria-hidden="true"> *</span></span>
          {draft.actions.length === 0 && <span className="t-sm muted">{t('adm.auto.noActions')}</span>}
          {draft.actions.map((action, index) => (
            <ActionEditor key={action.key} action={action} n={index + 1} codes={actionCodes}
                          params={catalog.actionParams?.[action.type]}
                          onChange={(next) => setDraft({ ...draft, actions: draft.actions.map((a) => (a.key === action.key ? next : a)) })}
                          onRemove={() => setDraft({ ...draft, actions: draft.actions.filter((a) => a.key !== action.key) })} />
          ))}
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn btn-ghost btn-sm" disabled={actionCodes.length === 0}
                    onClick={() => setDraft({
                      ...draft, actions: [...draft.actions, { key: nextKey(), type: actionCodes[0] ?? '', params: {} }],
                    })}>
              <Icon name="plus" size="sm" />{t('adm.auto.addAction')}
            </button>
            <span className="t-xs muted">{t('adm.auto.placeholders')}</span>
          </div>
        </div>

        <Switch checked={draft.active} label={t('adm.auto.activeLabel')} onChange={(active) => setDraft({ ...draft, active })} />
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}

function TimerEditor({ timer, entity, onChange }: {
  timer: TimerDraft
  entity: AutomationEntity | undefined
  onChange: (timer: TimerDraft) => void
}) {
  const { t } = useApp()
  return (
    <div className="field" style={{ gap: 6 }}>
      <span className="label">{t('adm.auto.timer')}</span>
      <div className="row-wrap" style={{ gap: 6 }}>
        {timer.custom != null ? (
          <input className="input input-sm mono" aria-label={t('adm.auto.timerCustom')} value={timer.custom}
                 onChange={(e) => onChange({ ...timer, custom: e.target.value })} />
        ) : (
          <>
            <input className="input input-sm" style={{ width: 80 }} inputMode="numeric" aria-label={t('adm.auto.timerAmount')}
                   value={timer.amount} onChange={(e) => onChange({ ...timer, amount: e.target.value })} />
            <select className="input input-sm" aria-label={t('adm.auto.timerUnit')} value={timer.unit}
                    onChange={(e) => onChange({ ...timer, unit: e.target.value as TimerDraft['unit'] })}>
              {(['M', 'H', 'D'] as const).map((unit) => <option key={unit} value={unit}>{t(`adm.auto.unit.${unit}`)}</option>)}
            </select>
            <select className="input input-sm" aria-label={t('adm.auto.timerDirection')} value={timer.direction}
                    onChange={(e) => onChange({ ...timer, direction: e.target.value as TimerDraft['direction'] })}>
              <option value="after">{t('adm.auto.after')}</option>
              <option value="before">{t('adm.auto.before')}</option>
            </select>
          </>
        )}
        <select className="input input-sm" aria-label={t('adm.auto.timerAnchor')} value={timer.anchor}
                onChange={(e) => onChange({ ...timer, anchor: e.target.value })}>
          {(entity?.anchors ?? []).map((anchor) => <option key={anchor.name} value={anchor.name}>{anchor.label}</option>)}
        </select>
      </div>
      <span className="help">{t('adm.auto.timer.help')}</span>
    </div>
  )
}

function GroupEditor({ group, path, entity, trigger, catalog, onChange, onRemove }: {
  group: CondGroup
  /** «» у корня, «2» у второй вложенной группы: подписи полей различаются по пути. */
  path: string
  entity: AutomationEntity | undefined
  trigger: string
  catalog: AutomationCatalog
  onChange: (group: CondGroup) => void
  onRemove?: () => void
}) {
  const { t } = useApp()
  const fields = entity?.fields ?? []
  const firstField = fields[0]
  const newLeaf = (): CondLeaf => ({
    kind: 'leaf', key: nextKey(), field: firstField?.name ?? '', op: firstField?.operators?.[0] ?? 'eq', value: '',
  })
  const replace = (key: string, item: CondLeaf | CondGroup | null) =>
    onChange({ ...group, items: item ? group.items.map((i) => (i.key === key ? item : i)) : group.items.filter((i) => i.key !== key) })
  const where = path === '' ? t('adm.auto.rootGroup') : t('adm.auto.groupN', { n: path })
  return (
    <div className={path === '' ? 'stack' : 'stack panel'} style={{ gap: 6, padding: path === '' ? 0 : 10 }}
         role="group" aria-label={where}>
      <div className="row" style={{ gap: 8 }}>
        <select className="input input-sm" style={{ width: 'auto' }} aria-label={t('adm.auto.modeOf', { where })} value={group.mode}
                onChange={(e) => onChange({ ...group, mode: e.target.value as CondGroup['mode'] })}>
          <option value="all">{t('adm.auto.mode.all')}</option>
          <option value="any">{t('adm.auto.mode.any')}</option>
        </select>
        {group.items.length === 0 && <span className="t-sm muted">{path === '' ? t('adm.auto.always') : t('adm.auto.emptyGroup')}</span>}
        <span className="spacer" />
        {onRemove && (
          <button type="button" className="btn btn-ghost btn-sm" aria-label={t('adm.auto.removeGroup', { n: path })} onClick={onRemove}>
            <Icon name="close" size="sm" />
          </button>
        )}
      </div>
      {group.items.map((item, index) => {
        const itemPath = path === '' ? String(index + 1) : `${path}.${index + 1}`
        return item.kind === 'leaf' ? (
          <LeafEditor key={item.key} leaf={item} path={itemPath} join={index === 0 ? t('adm.auto.if')
            : group.mode === 'all' ? t('adm.auto.and') : t('adm.auto.or')}
                      entity={entity} trigger={trigger} catalog={catalog}
                      onChange={(leaf) => replace(item.key, leaf)} onRemove={() => replace(item.key, null)} />
        ) : (
          <GroupEditor key={item.key} group={item} path={itemPath} entity={entity} trigger={trigger} catalog={catalog}
                       onChange={(next) => replace(item.key, next)} onRemove={() => replace(item.key, null)} />
        )
      })}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn btn-ghost btn-sm" disabled={fields.length === 0}
                aria-label={t('adm.auto.addConditionTo', { where })}
                onClick={() => onChange({ ...group, items: [...group.items, newLeaf()] })}>
          <Icon name="plus" size="sm" />{t('adm.auto.addCondition')}
        </button>
        {path === '' && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={fields.length === 0}
                  onClick={() => onChange({ ...group, items: [...group.items, { ...emptyGroup(group.mode === 'all' ? 'any' : 'all'), items: [newLeaf()] }] })}>
            <Icon name="plus" size="sm" />{t('adm.auto.addGroup')}
          </button>
        )}
      </div>
    </div>
  )
}

function LeafEditor({ leaf, path, join, entity, trigger, catalog, onChange, onRemove }: {
  leaf: CondLeaf
  path: string
  join: string
  entity: AutomationEntity | undefined
  trigger: string
  catalog: AutomationCatalog
  onChange: (leaf: CondLeaf) => void
  onRemove: () => void
}) {
  const { t } = useApp()
  const fields = entity?.fields ?? []
  const base = leaf.field.startsWith(PREVIOUS) ? leaf.field.slice(PREVIOUS.length) : leaf.field
  const field = fields.find((candidate) => candidate.name === base)
  const operators = field?.operators ?? (catalog.operators ?? []).map((op) => op.code)
  const type = fieldType(entity, leaf.field)
  // "previous.<field>" exists only where a change is reported: field_changed, trackable fields.
  const previous = trigger === 'field_changed' ? fields.filter((candidate) => candidate.trackable) : []
  const known = fields.some((candidate) => candidate.name === leaf.field)
    || previous.some((candidate) => PREVIOUS + candidate.name === leaf.field)
  const setField = (name: string) => {
    const next = fields.find((candidate) => candidate.name === (name.startsWith(PREVIOUS) ? name.slice(PREVIOUS.length) : name))
    const ops = next?.operators ?? operators
    onChange({ ...leaf, field: name, op: ops.includes(leaf.op) ? leaf.op : (ops[0] ?? 'eq'), value: '' })
  }
  const valueLabel = t('adm.auto.condValue', { n: path })
  let value: ReactNode
  if (NO_VALUE_OPS.has(leaf.op)) {
    value = <span />
  } else if (type === 'boolean' && !LIST_OPS.has(leaf.op)) {
    value = (
      <select className="input input-sm" aria-label={valueLabel} value={leaf.value} onChange={(e) => onChange({ ...leaf, value: e.target.value })}>
        <option value="">—</option>
        <option value="true">{t('adm.auto.true')}</option>
        <option value="false">{t('adm.auto.false')}</option>
      </select>
    )
  } else {
    value = (
      <input className="input input-sm" aria-label={valueLabel} value={leaf.value}
             type={type === 'date' && !LIST_OPS.has(leaf.op) ? 'date' : 'text'}
             inputMode={type === 'number' ? 'decimal' : undefined}
             placeholder={LIST_OPS.has(leaf.op) ? t('adm.auto.listHint') : type === 'datetime' ? '2026-10-10T09:00:00+03:00' : undefined}
             onChange={(e) => onChange({ ...leaf, value: e.target.value })} />
    )
  }
  return (
    <div className="adm-cond">
      <span className="fb-join">{join}</span>
      <select className="input input-sm" aria-label={t('adm.auto.condField', { n: path })} value={leaf.field}
              onChange={(e) => setField(e.target.value)}>
        {!known && <option value={leaf.field}>{leaf.field}</option>}
        {fields.map((candidate) => <option key={candidate.name} value={candidate.name}>{candidate.label}</option>)}
        {previous.map((candidate) => (
          <option key={PREVIOUS + candidate.name} value={PREVIOUS + candidate.name}>{t('adm.auto.previous', { label: candidate.label })}</option>
        ))}
      </select>
      <select className="input input-sm" aria-label={t('adm.auto.condOp', { n: path })} value={leaf.op}
              onChange={(e) => onChange({ ...leaf, op: e.target.value, value: NO_VALUE_OPS.has(e.target.value) ? '' : leaf.value })}>
        {!operators.includes(leaf.op) && <option value={leaf.op}>{leaf.op}</option>}
        {operators.map((code) => <option key={code} value={code}>{opSign(catalog, code)}</option>)}
      </select>
      {value}
      <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('adm.auto.condRemove', { n: path })} onClick={onRemove}>
        <Icon name="close" size="sm" />
      </button>
    </div>
  )
}

function ActionEditor({ action, n, codes, params, onChange, onRemove }: {
  action: ActionDraft
  n: number
  codes: string[]
  params: AutomationActionParam[] | undefined
  onChange: (action: ActionDraft) => void
  onRemove: () => void
}) {
  const { t } = useApp()
  const actionLabel = label(t, `adm.auto.action.${action.type}`, action.type)
  const set = (name: string, value: string) => onChange({ ...action, params: { ...action.params, [name]: value } })
  return (
    <div className="panel stack" style={{ gap: 8, padding: 10 }} role="group" aria-label={t('adm.auto.actionN', { n })}>
      <div className="row" style={{ gap: 8 }}>
        <span className="ord">{n}</span>
        <select className="input input-sm" style={{ width: 'auto' }} aria-label={t('adm.auto.actionType', { n })} value={action.type}
                // Another action reads other parameters: the old ones would be sent and ignored.
                onChange={(e) => onChange({ ...action, type: e.target.value, params: {} })}>
          {!codes.includes(action.type) && <option value={action.type}>{action.type}</option>}
          {codes.map((code) => <option key={code} value={code}>{label(t, `adm.auto.action.${code}`, code)}</option>)}
        </select>
        <span className="spacer" />
        <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('adm.auto.actionRemove', { n })} onClick={onRemove}>
          <Icon name="close" size="sm" />
        </button>
      </div>
      {(params ?? []).map((param) => {
        const paramLabel = label(t, `adm.auto.param.${action.type}.${param.name}`, param.name)
        const aria = `${actionLabel} ${n}: ${paramLabel}`
        return (
          <Field key={param.name} label={paramLabel} required={param.required}>
            <ParamInput param={param} aria={aria} type={action.type} value={action.params[param.name] ?? ''}
                        onChange={(value) => set(param.name, value)} />
          </Field>
        )
      })}
    </div>
  )
}

function ParamInput({ param, aria, type, value, onChange }: {
  param: AutomationActionParam
  aria: string
  type: string
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useApp()
  const id = useId()
  switch (param.kind) {
    case 'longtext':
      return <textarea className="input" rows={3} aria-label={aria} value={value} onChange={(e) => onChange(e.target.value)} />
    case 'hours':
      return <input className="input input-sm" style={{ width: 120 }} inputMode="numeric" aria-label={aria} value={value}
                    onChange={(e) => onChange(e.target.value)} />
    case 'choice':
      return (
        <select className="input input-sm" aria-label={aria} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(param.options ?? []).map((option) => (
            <option key={option} value={option}>{label(t, `adm.auto.option.${type}.${param.name}.${option}`, option)}</option>
          ))}
        </select>
      )
    case 'employee':
      return (
        <span aria-label={aria} role="group">
          <RecordPicker id={id} entity="hr.employee" searchColumn="last_name" extraColumn="first_name"
                        value={value ? { id: value, label: value } : null} onChange={(picked) => onChange(picked?.id ?? '')} />
        </span>
      )
    case 'recipient':
      return <RecipientInput aria={aria} value={value} onChange={onChange} />
    case 'template':
      return <TemplateSelect aria={aria} value={value} onChange={onChange} />
    default:
      return <input className="input input-sm" aria-label={aria} value={value} type={param.kind === 'url' ? 'url' : 'text'}
                    placeholder={param.kind === 'url' ? 'https://' : undefined} onChange={(e) => onChange(e.target.value)} />
  }
}

/** «responsible», «manager», «role:КОД», «user:id» — одна строка правила, два поля на экране. */
function RecipientInput({ aria, value, onChange }: { aria: string; value: string; onChange: (value: string) => void }) {
  const { t } = useApp()
  const mode = value.startsWith('role:') ? 'role' : value.startsWith('user:') ? 'user' : value
  const rest = mode === 'role' || mode === 'user' ? value.slice(mode.length + 1) : ''
  return (
    <span className="row" style={{ gap: 6 }}>
      <select className="input input-sm" aria-label={aria} value={mode}
              onChange={(e) => onChange(e.target.value === 'role' || e.target.value === 'user' ? `${e.target.value}:` : e.target.value)}>
        <option value="">—</option>
        {['responsible', 'manager', 'role', 'user'].map((option) => (
          <option key={option} value={option}>{t(`adm.auto.recipient.${option}`)}</option>
        ))}
      </select>
      {(mode === 'role' || mode === 'user') && (
        <input className="input input-sm mono" aria-label={t(`adm.auto.recipient.${mode}.value`)} value={rest}
               placeholder={mode === 'role' ? 'SALES_HEAD' : 'uuid'} onChange={(e) => onChange(`${mode}:${e.target.value}`)} />
      )}
    </span>
  )
}

function TemplateSelect({ aria, value, onChange }: { aria: string; value: string; onChange: (value: string) => void }) {
  const { t } = useApp()
  const templates = useQuery({ queryKey: ['automation-templates'], queryFn: ({ signal }) => fetchTemplatesFor('deal', signal) })
  const list = (templates.data ?? []).filter((template) => template.active)
  return (
    <>
      <select className="input input-sm" aria-label={aria} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {value && !list.some((template) => template.id === value) && <option value={value}>{value}</option>}
        {list.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
      </select>
      {templates.isError && <span className="t-sm muted" role="alert">{failureText(t, templates.error)}</span>}
    </>
  )
}

// ------------------------------------------------------------------ journal

function moment(value: string | number | null | undefined, locale: string): string {
  if (value == null) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
}

function RunOutcome({ run }: { run: AutomationRun }) {
  const { t } = useApp()
  if (run.error != null) return <span className="badge b-danger"><Icon name="alert" />{t('adm.auto.run.failed')}</span>
  if (run.matched) return <span className="badge b-success"><Icon name="check" />{t('adm.auto.run.matched')}</span>
  return <span className="badge b-warning"><Icon name="close" />{t('adm.auto.run.notMatched')}</span>
}

function JournalSheet({ rule, entity, catalog, onClose }: {
  rule: AutomationRule
  entity: AutomationEntity | undefined
  catalog: AutomationCatalog | undefined
  onClose: () => void
}) {
  const { t, locale } = useApp()
  const [record, setRecord] = useState<{ id: string; label: string } | null>(null)
  const [typedId, setTypedId] = useState('')
  const runs = useQuery({
    queryKey: ['admin-automation-runs', rule.id, record?.id ?? null],
    queryFn: ({ signal }) => fetchAutomationRuns(rule.id, record?.id ?? null, signal),
  })
  const evaluate = useMutation({ mutationFn: (entityId: string) => evaluateAutomationRule(rule.id, entityId) })
  const picker = PICKERS[rule.entityType]
  const choose = (next: { id: string; label: string } | null) => {
    evaluate.reset()
    setRecord(next)
  }

  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t('adm.auto.journal')}
      title={rule.name}
      subtitle={`${entity?.label ?? rule.entityType} · ${label(t, `adm.auto.trigger.${rule.trigger}`, rule.trigger)}`}
      onClose={onClose}
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>{t('adm.close')}</button>}
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field label={t('adm.auto.record')} htmlFor="auto-record" help={t('adm.auto.record.help')}>
          {record ? (
            <span className="row" style={{ gap: 8 }}>
              <span className="strong" data-testid="journal-record">{record.label}</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => choose(null)}>{t('adm.auto.allRecords')}</button>
            </span>
          ) : picker ? (
            <RecordPicker id="auto-record" entity={picker.entity} searchColumn={picker.search} extraColumn={picker.extra}
                          value={null} onChange={choose} />
          ) : (
            <span className="row" style={{ gap: 6 }}>
              <input id="auto-record" className="input input-sm mono" placeholder="uuid" value={typedId}
                     onChange={(e) => setTypedId(e.target.value)} />
              <button type="button" className="btn btn-secondary btn-sm" disabled={typedId.trim() === ''}
                      onClick={() => choose({ id: typedId.trim(), label: typedId.trim() })}>{t('adm.auto.show')}</button>
            </span>
          )}
        </Field>

        {record && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={evaluate.isPending}
                      onClick={() => evaluate.mutate(record.id)}>
                <Icon name="check" size="sm" />{t('adm.auto.evaluate')}
              </button>
              <span className="t-xs muted">{t('adm.auto.evaluate.help')}</span>
            </div>
            {evaluate.isError && <Notice text={failureText(t, evaluate.error)} />}
            {evaluate.data && (
              <section className="panel stack" style={{ gap: 6, padding: 12 }} aria-label={t('adm.auto.verdict')}>
                <span className="strong">
                  {evaluate.data.matched ? t('adm.auto.wouldFire') : t('adm.auto.wouldNotFire')}
                </span>
                {evaluate.data.explanation && <ExplanationTree node={evaluate.data.explanation} catalog={catalog} entity={entity} root />}
                {!evaluate.data.explanation && evaluate.data.reason != null && <span>{evaluate.data.reason}</span>}
              </section>
            )}
          </div>
        )}

        <AdminState isLoading={runs.isLoading} error={runs.error} onRetry={() => void runs.refetch()} skeleton={<SkeletonList rows={3} />}>
          {runs.data && runs.data.runs.length === 0 ? (
            <StateView kind="empty" title={record ? t('adm.auto.noRunsOnRecord') : t('adm.auto.noRuns')}
                       description={t('adm.auto.noRuns.text')} />
          ) : (
            <div className="tbl-wrap">
              <table className="tbl" aria-label={t('adm.auto.runs')}>
                <thead>
                  <tr>
                    <th>{t('adm.auto.col.when')}</th>
                    <th>{t('adm.auto.col.record')}</th>
                    <th>{t('adm.auto.col.result')}</th>
                    <th>{t('adm.auto.col.why')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(runs.data?.runs ?? []).map((run) => (
                    <tr key={String(run.id)} data-testid={`run-${run.id}`}>
                      <td className="nowrap">{moment(run.ran_at, locale)}</td>
                      <td>
                        {record ? (run.entity_label ?? run.entity_id) : (
                          <button type="button" className="btn btn-ghost btn-sm"
                                  aria-label={t('adm.auto.runsOf', { record: run.entity_label ?? run.entity_id })}
                                  onClick={() => choose({ id: run.entity_id, label: run.entity_label ?? run.entity_id })}>
                            {run.entity_label ?? run.entity_id}
                          </button>
                        )}
                      </td>
                      <td><RunOutcome run={run} /></td>
                      <td>
                        {run.error ?? run.reason ?? (run.matched
                          ? t('adm.auto.run.acted', { count: run.actions_run ?? 0 })
                          : '—')}
                        {run.depth != null && run.depth > 0 && (
                          <span className="t-xs muted"> · {t('adm.auto.run.depth', { depth: run.depth })}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(runs.data?.actions ?? []).length > 0 && (
            <div className="tbl-wrap">
              <table className="tbl" aria-label={t('adm.auto.actionRuns')}>
                <thead>
                  <tr>
                    <th>{t('adm.auto.col.when')}</th>
                    <th>{t('adm.auto.col.record')}</th>
                    <th>{t('adm.auto.col.action')}</th>
                    <th>{t('adm.auto.col.outcome')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(runs.data?.actions ?? []).map((action) => (
                    <tr key={String(action.id)}>
                      <td className="nowrap">{moment(action.ran_at, locale)}</td>
                      <td>{action.entity_label ?? action.entity_id}</td>
                      <td>{label(t, `adm.auto.action.${action.action_type}`, action.action_type)}</td>
                      <td>
                        {label(t, `adm.auto.outcome.${action.outcome}`, action.outcome)}
                        {action.detail != null && <span className="t-xs muted"> · {action.detail}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminState>
      </div>
    </EditLayer>
  )
}

function ExplanationTree({ node, catalog, entity, root }: {
  node: AutomationExplanation
  catalog: AutomationCatalog | undefined
  entity: AutomationEntity | undefined
  root?: boolean
}) {
  const { t } = useApp()
  const mark = (
    <span className={node.matched ? 'badge b-success' : 'badge b-danger'}>
      <Icon name={node.matched ? 'check' : 'close'} />{node.matched ? t('adm.auto.met') : t('adm.auto.notMet')}
    </span>
  )
  if (node.kind === 'leaf') {
    const field = node.field == null ? '' : fieldLabel(t, entity, node.field)
    return (
      <li className="row-wrap" style={{ gap: 6 }} data-testid={`explain-${node.field ?? ''}`}>
        {mark}
        <span className="mono">{node.condition}</span>
        {field && <span className="t-sm muted">({field})</span>}
        <span className="t-sm">
          {/* A failed condition is explained in the server's words ("значение «5000»", "поле не
              менялось"); a met one shows the value it was met with. */}
          {node.reason != null && node.reason.startsWith(`${node.condition}: `)
            ? node.reason.slice(node.condition.length + 2)
            : node.actual == null ? t('adm.auto.actualNone') : t('adm.auto.actual', { value: String(node.actual) })}
        </span>
      </li>
    )
  }
  const items = node.items ?? []
  const body = (
    <>
      <span className="row" style={{ gap: 6 }}>
        {mark}
        <span className="t-sm secondary">{t(`adm.auto.kind.${node.kind}`)}</span>
      </span>
      <ul className="stack" style={{ gap: 4, paddingLeft: 16, margin: 0, listStyle: 'none' }}>
        {items.map((item, index) => (
          <Fragment key={index}>
            <ExplanationTree node={item} catalog={catalog} entity={entity} />
          </Fragment>
        ))}
        {items.length === 0 && <li className="t-sm muted">{t('adm.auto.always')}</li>}
      </ul>
    </>
  )
  return root ? <div className="stack" style={{ gap: 4 }}>{body}</div> : <li className="stack" style={{ gap: 4 }}>{body}</li>
}
